import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Confirm, ErrorState, Field, Modal, Spinner } from '../../components/ui.jsx';
import {
  ChargeTable,
  LATE_FEE_REASONS,
  LeaseDocument,
  LeaseStatus,
  LedgerSummary,
  PaymentTable,
  SignLeaseModal
} from '../../components/lease.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { DepositPanel } from '../../components/refunds.jsx';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatXaf, monthsBetween, rentPeriodMonths, rentPeriodNoun } from '../../lib/format.js';

const todayIso = () => new Date().toISOString().slice(0, 10);

const monthSpan = (fromMonth, toMonth) => {
  if (!fromMonth || !toMonth || fromMonth > toMonth) return 0;
  const [fromYear, fromMon] = fromMonth.split('-').map(Number);
  const [toYear, toMon] = toMonth.split('-').map(Number);
  return (toYear - fromYear) * 12 + (toMon - fromMon) + 1;
};

const RecordPaymentModal = ({ tenancy, charge, onClose, onDone }) => {
  const toast = useToast();
  const { paymentMethods } = useMeta();
  // What the landlord most likely has in hand: the charge they clicked, else the
  // oldest one still owing, else whatever the account is short overall.
  const outstanding =
    charge?.outstanding ??
    tenancy.ledger.totals.nextDue?.outstanding ??
    Math.max(tenancy.ledger.totals.balance, 0);

  const [form, setForm] = useState({
    amount: String(Math.max(outstanding, 0) || ''),
    method: 'cash',
    reference: '',
    paidAt: todayIso(),
    note: '',
    billKind: '',
    periodFrom: '',
    periodTo: ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const selectedMonths = monthSpan(form.periodFrom, form.periodTo);
  const yearlyRentOutstanding = (tenancy.ledger?.charges || [])
    .filter((item) => item.kind === 'rent' && (item.outstanding || 0) > 0)
    .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))[0]?.outstanding;
  const depositOutstanding = (tenancy.ledger?.charges || [])
    .filter((item) => item.kind === 'deposit' && (item.outstanding || 0) > 0)
    .reduce((sum, item) => sum + (item.outstanding || 0), 0);
  const configuredDeposit = tenancy.deposit || 0;
  const yearlyRentMode = form.billKind === 'rent' && tenancy.billingPeriod === 'yearly';
  const autoRate =
    form.billKind === 'rent'
      ? Math.round(tenancy.rent / rentPeriodMonths(tenancy.billingPeriod))
      : form.billKind === 'internet'
        ? tenancy.internet || 0
        : 0;
  const autoAmount =
    form.billKind === 'deposit'
      ? (depositOutstanding > 0 ? depositOutstanding : configuredDeposit > 0 ? configuredDeposit : null)
      : yearlyRentMode
        ? yearlyRentOutstanding || tenancy.rent
      : form.billKind && selectedMonths > 0
        ? autoRate * selectedMonths
        : null;

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});

    try {
      const result = await api.post(`/tenancies/${tenancy.id}/payments`, {
        amount: form.amount ? Number(form.amount) : undefined,
        method: form.method,
        chargeId: charge?.id ?? null,
        reference: form.reference.trim() || null,
        paidAt: form.paidAt,
        note: form.note.trim() || null,
        billKind: charge ? null : form.billKind || null,
        periodFrom: charge ? null : form.periodFrom || null,
        periodTo: charge ? null : form.periodTo || null
      });

      if (Array.isArray(result.payments) && result.payments.length > 1) {
        toast.success(`${result.payments.length} receipts issued.`);
      } else {
        toast.success(`Receipt ${result.payment.receiptNumber} issued.`);
      }
      onDone(result.payment);
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={charge ? `Record payment for ${charge.label}` : 'Record a payment'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="payment-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Record it and issue a receipt'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        For money that has already reached you: cash in hand, a mobile money transfer to your own number, or a bank
        deposit. A numbered receipt is issued and the tenant is notified.
        {charge ? ` ${formatXaf(charge.outstanding)} is outstanding on this charge.` : ''}
      </p>

      <form id="payment-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="Amount received in FCFA" error={errors.amount} required>
            <input
              type="number"
              min="1"
              step="1"
              value={form.amount}
              onChange={(event) => set({ amount: event.target.value })}
              required
              autoFocus
            />
          </Field>

          <Field label="Date received" error={errors.paidAt}>
            <input type="date" value={form.paidAt} onChange={(event) => set({ paidAt: event.target.value })} />
          </Field>
        </div>

        <div className="grid-2">
          <Field label="How it was paid" error={errors.method} required>
            <select value={form.method} onChange={(event) => set({ method: event.target.value })}>
              {paymentMethods.map((method) => (
                <option key={method.slug} value={method.slug}>
                  {method.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Reference"
            hint="Transfer or transaction number, teller slip."
            error={errors.reference}
          >
            <input type="text" value={form.reference} onChange={(event) => set({ reference: event.target.value })} />
          </Field>
        </div>

        <Field label="Note on the receipt" error={errors.note}>
          <input type="text" value={form.note} onChange={(event) => set({ note: event.target.value })} />
        </Field>

        {!charge ? (
          <Field
            label="Calculate from"
            hint="Pick rent or internet bill to auto-calculate from selected months."
            error={errors.billKind}
          >
            <select
              value={form.billKind}
              onChange={(event) => {
                const nextKind = event.target.value;
                if (!nextKind) {
                  set({ billKind: '', amount: String(Math.max(outstanding, 0) || ''), periodFrom: '', periodTo: '' });
                  return;
                }
                if (nextKind === 'deposit') {
                  const suggested = depositOutstanding > 0 ? depositOutstanding : configuredDeposit;
                  set({ billKind: nextKind, periodFrom: '', periodTo: '', amount: suggested > 0 ? String(suggested) : '' });
                  return;
                }
                if (nextKind === 'rent' && tenancy.billingPeriod === 'yearly') {
                  const suggested = yearlyRentOutstanding || tenancy.rent;
                  set({ billKind: nextKind, periodFrom: '', periodTo: '', amount: suggested > 0 ? String(suggested) : '' });
                  return;
                }
                const rate =
                  nextKind === 'rent'
                    ? Math.round(tenancy.rent / rentPeriodMonths(tenancy.billingPeriod))
                    : tenancy.internet || 0;
                const suggested = selectedMonths > 0 ? rate * selectedMonths : null;
                set({ billKind: nextKind, amount: suggested ? String(suggested) : '' });
              }}
            >
              <option value="">Manual amount</option>
              <option value="rent">Tenant rent</option>
              <option value="internet" disabled={!tenancy.internet || tenancy.internet <= 0}>
                Internet bill
              </option>
              <option value="deposit" disabled={configuredDeposit <= 0}>
                Deposit
              </option>
            </select>
          </Field>
        ) : null}

        {!charge ? (
          <div className="grid-2">
            <Field
              label="From month"
              hint="Choose months to apply this payment range."
              error={errors.periodFrom}
            >
              <input
                type="month"
                value={form.periodFrom}
                onChange={(event) => {
                  const nextFrom = event.target.value;
                  const months = monthSpan(nextFrom, form.periodTo);
                  const shouldAuto = form.billKind === 'rent' || form.billKind === 'internet';
                  const rate = form.billKind === 'rent' ? tenancy.rent : tenancy.internet || 0;
                  set({
                    periodFrom: nextFrom,
                    amount: shouldAuto && months > 0 ? String(rate * months) : form.amount
                  });
                }}
                disabled={form.billKind === 'deposit' || yearlyRentMode}
              />
            </Field>
            <Field label="To month" error={errors.periodTo}>
              <input
                type="month"
                value={form.periodTo}
                onChange={(event) => {
                  const nextTo = event.target.value;
                  const months = monthSpan(form.periodFrom, nextTo);
                  const shouldAuto = form.billKind === 'rent' || form.billKind === 'internet';
                  const rate = form.billKind === 'rent' ? tenancy.rent : tenancy.internet || 0;
                  set({
                    periodTo: nextTo,
                    amount: shouldAuto && months > 0 ? String(rate * months) : form.amount
                  });
                }}
                disabled={form.billKind === 'deposit' || yearlyRentMode}
              />
            </Field>
          </div>
        ) : null}

        {!charge && form.billKind ? (
          <p className="field-hint">
            {form.billKind === 'deposit'
              ? `Auto total for outstanding deposit: ${formatXaf(autoAmount || 0)}.`
              : yearlyRentMode
              ? `Yearly rent cycle detected. Suggested amount: ${formatXaf(autoAmount || 0)}. You can edit the amount to record a partial payment.`
              : selectedMonths > 0
              ? `${selectedMonths} month${selectedMonths === 1 ? '' : 's'} selected at ${formatXaf(autoRate)} per month. Auto total: ${formatXaf(autoAmount)}.`
              : 'Select both months to calculate automatically.'}
          </p>
        ) : null}

        {!charge ? (
          <p className="field-hint">
            This will be applied to the oldest things outstanding. Set a month range to post it against specific rent
            months.
          </p>
        ) : null}
      </form>
    </Modal>
  );
};

const AddChargeModal = ({ tenancy, onClose, onDone }) => {
  const toast = useToast();
  const { chargeKinds } = useMeta();
  const [form, setForm] = useState({ kind: 'electricity', label: '', amount: '', dueDate: todayIso() });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await api.post(`/tenancies/${tenancy.id}/charges`, {
        kind: form.kind,
        label: form.label.trim(),
        amount: Number(form.amount),
        dueDate: form.dueDate
      });
      toast.success('Charge added and the tenant notified.');
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Add a charge"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="charge-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Adding...' : 'Add the charge'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        Rent is generated from the lease automatically. Use this for a water or electricity bill, a repair the tenant
        caused, or a late payment penalty.
      </p>

      <form id="charge-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="What kind" error={errors.kind}>
            <select value={form.kind} onChange={(event) => set({ kind: event.target.value })}>
              {chargeKinds
                .filter((kind) => kind.slug !== 'rent' && kind.slug !== 'deposit')
                .map((kind) => (
                  <option key={kind.slug} value={kind.slug}>
                    {kind.label}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Amount in FCFA" error={errors.amount} required>
            <input
              type="number"
              min="1"
              step="1"
              value={form.amount}
              onChange={(event) => set({ amount: event.target.value })}
              required
            />
          </Field>
        </div>

        <Field label="What it is for" error={errors.label} required>
          <input
            type="text"
            value={form.label}
            onChange={(event) => set({ label: event.target.value })}
            placeholder="CAMWATER bill for March"
            required
          />
        </Field>

        <Field label="Due by" error={errors.dueDate} required>
          <input type="date" value={form.dueDate} onChange={(event) => set({ dueDate: event.target.value })} required />
        </Field>
      </form>
    </Modal>
  );
};

const InternetOfferModal = ({ tenancy, onClose, onDone }) => {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post(`/tenancies/${tenancy.id}/internet-offer`, { amount: Number(amount) });
      toast.success('Internet offer sent. It applies once the tenant accepts.');
      onDone();
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.details) setError(requestError.details.amount || requestError.message);
      else setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Offer internet"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="internet-offer-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Sending...' : 'Send the offer'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        {tenancy.tenant?.name} sees this offer and has to accept it before anything is billed. Billing starts from the
        day they accept, due on the 27th of every month - nothing is charged for months already passed.
      </p>
      <form id="internet-offer-form" className="stack" onSubmit={submit}>
        <Field label="Monthly amount in FCFA" error={error} required>
          <input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            required
            autoFocus
          />
        </Field>
      </form>
    </Modal>
  );
};

/** Edits a draft. Anything already signed has to be withdrawn before it opens. */
const EditLeaseModal = ({ tenancy, onClose, onDone }) => {
  const toast = useToast();
  const { billingPeriods } = useMeta();
  const [form, setForm] = useState({
    startDate: tenancy.startDate,
    endDate: tenancy.endDate || '',
    rent: String(tenancy.rent),
    deposit: String(tenancy.deposit),
    billingPeriod: tenancy.billingPeriod,
    rentDueDay: tenancy.rentDueDay,
    specialConditions: tenancy.specialConditions || '',
    terms: tenancy.terms
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await api.patch(`/tenancies/${tenancy.id}`, {
        startDate: form.startDate,
        endDate: form.endDate || null,
        rent: Number(form.rent),
        deposit: Number(form.deposit || 0),
        billingPeriod: form.billingPeriod,
        rentDueDay: Number(form.rentDueDay),
        specialConditions: form.specialConditions.trim() || null,
        terms: form.terms
      });
      toast.success('Lease updated.');
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Edit the lease"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="lease-edit-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Save the lease'}
          </button>
        </>
      }
    >
      <form id="lease-edit-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="Tenancy starts" error={errors.startDate} required>
            <input type="date" value={form.startDate} onChange={(event) => set({ startDate: event.target.value })} required />
          </Field>
          <Field label="Tenancy ends" hint="Empty means month to month." error={errors.endDate}>
            <input type="date" value={form.endDate} onChange={(event) => set({ endDate: event.target.value })} />
          </Field>
        </div>

        <div className="grid-3">
          <Field label="Rent in FCFA" error={errors.rent} required>
            <input
              type="number"
              min="0"
              step="1"
              value={form.rent}
              onChange={(event) => set({ rent: event.target.value })}
              required
            />
          </Field>
          <Field label="Paid" error={errors.billingPeriod}>
            <select value={form.billingPeriod} onChange={(event) => set({ billingPeriod: event.target.value })}>
              {billingPeriods.map((period) => (
                <option key={period.slug} value={period.slug}>
                  {period.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due on day" error={errors.rentDueDay}>
            <input
              type="number"
              min="1"
              max="28"
              value={form.rentDueDay}
              onChange={(event) => set({ rentDueDay: event.target.value })}
            />
          </Field>
        </div>

        <Field label="Deposit in FCFA" error={errors.deposit}>
          <input
            type="number"
            min="0"
            step="1"
            value={form.deposit}
            onChange={(event) => set({ deposit: event.target.value })}
          />
        </Field>

        <Field label="Conditions specific to this tenancy" error={errors.specialConditions}>
          <textarea
            rows="3"
            value={form.specialConditions}
            onChange={(event) => set({ specialConditions: event.target.value })}
          />
        </Field>

        <Field label="The agreement" hint="Edit any clause. This is what both parties sign." error={errors.terms}>
          <textarea rows="16" className="mono" value={form.terms} onChange={(event) => set({ terms: event.target.value })} />
        </Field>
      </form>
    </Modal>
  );
};

const ReasonModal = ({ title, lead, label, confirmLabel, tone = 'primary', suggestions = [], onSubmit, onClose }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await onSubmit(reason.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="reason-form"
            className={`btn btn-${tone}`}
            disabled={busy || reason.trim().length < 2}
          >
            {busy ? 'Saving...' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="modal-lead">{lead}</p>
      <form id="reason-form" onSubmit={submit}>
        <Field label={label} required>
          <textarea rows="3" value={reason} onChange={(event) => setReason(event.target.value)} autoFocus />
        </Field>
        {suggestions.length ? (
          <div className="reason-chips" role="group" aria-label="Common reasons">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className={`reason-chip ${reason === suggestion ? 'is-active' : ''}`}
                onClick={() => setReason(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        ) : null}
      </form>
    </Modal>
  );
};

const LeaseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const { platform } = useMeta();
  const { data, loading, error, reload } = useFetch(`/tenancies/${id}`, [id]);
  const awaiting = useFetch(`/tenancies/${id}/payment-intents/awaiting-confirmation`, [id]);

  const [dialog, setDialog] = useState(null);
  const [payingCharge, setPayingCharge] = useState(null);
  const [waiving, setWaiving] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmingIntentId, setConfirmingIntentId] = useState(null);

  useTitle(data ? `Lease NDK-L${String(data.tenancy.id).padStart(5, '0')}` : 'Lease');

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { tenancy } = data;
  const ledger = tenancy.ledger;
  const awaitingItems = awaiting.data?.items || [];
  const alreadySigned = Boolean(tenancy.signatures?.landlord);
  const noSignatures = !tenancy.signatures?.landlord && !tenancy.signatures?.tenant;

  const confirmTenantPayment = async (intent) => {
    setConfirmingIntentId(intent.id);
    try {
      await api.post(`/payment-intents/${intent.id}/confirm`, {});
      toast.success('Payment confirmed and posted to the statement.');
      awaiting.reload();
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setConfirmingIntentId(null);
    }
  };

  const move = async (status, extra = {}) => {
    try {
      await api.post(`/tenancies/${tenancy.id}/status`, { status, ...extra });
      toast.success('Lease updated.');
      setDialog(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  const waive = async (reason) => {
    try {
      await api.post(`/charges/${waiving.id}/waive`, { reason });
      toast.success(waiving.kind === 'penalty' ? 'Late fee waived. The tenant has been told why.' : 'Charge written off.');
      setWaiving(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  const voidReceipt = async (reason) => {
    try {
      await api.post(`/payments/${voiding.id}/void`, { reason });
      toast.success('Receipt cancelled.');
      setVoiding(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  const cancelInternetOffer = async () => {
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancy.id}/internet-offer/cancel`, {});
      toast.success('Internet offer withdrawn.');
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const endTenancy = async (reason) => {
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancy.id}/status`, { status: 'ended', reason, endedOn: todayIso() });
      toast.success('Tenancy ended. The unit is free again.');
      setDialog(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>
            {tenancy.unit?.label ? `${tenancy.unit.label}, ` : ''}
            {tenancy.property?.title}
          </h1>
          <p className="muted">
            <LeaseStatus status={tenancy.status} /> {tenancy.tenant?.name} - {formatXaf(tenancy.rent)} a{' '}
            {rentPeriodNoun(tenancy.billingPeriod)} from {formatDate(tenancy.startDate)}
            {tenancy.endDate ? (
              <>
                {' '}to {formatDate(tenancy.endDate)} ({monthsBetween(tenancy.startDate, tenancy.endDate)} month{monthsBetween(tenancy.startDate, tenancy.endDate) === 1 ? '' : 's'}, total {formatXaf(Math.round((tenancy.rent / rentPeriodMonths(tenancy.billingPeriod)) * monthsBetween(tenancy.startDate, tenancy.endDate)))})
              </>
            ) : null}
          </p>
        </div>
        <div className="row-actions">
          <Link to="/landlord/leases" className="btn btn-ghost">
            All leases
          </Link>
          <Link to={`/leases/${tenancy.id}/print`} className="btn btn-outline">
            Print the lease
          </Link>
          {['active', 'ended', 'terminated'].includes(tenancy.status) ? (
            <Link to={`/leases/${tenancy.id}/full-receipt`} className="btn btn-outline">
              Full rent receipt
            </Link>
          ) : null}
        </div>
      </header>

      {tenancy.status === 'draft' ? (
        <div className="notice notice-info">
          <strong>This is still a draft.</strong> Read it through and edit anything that is wrong, then send it out for
          signature. Nobody owes rent and nothing is binding until both of you have signed.
        </div>
      ) : null}

      {tenancy.status === 'awaiting_signatures' ? (
        <div className="notice notice-warn">
          <strong>Waiting for signatures.</strong>{' '}
          {alreadySigned
            ? `You have signed. ${tenancy.tenant?.name} still has to.`
            : 'You have not signed yet.'}{' '}
          Withdrawing it for changes clears any signature already given.
        </div>
      ) : null}

      {tenancy.status === 'active' && tenancy.internetOffer ? (
        <div className="notice notice-info">
          <strong>Internet offer of {formatXaf(tenancy.internetOffer)} a month sent to {tenancy.tenant?.name}.</strong>{' '}
          Nothing is billed until they accept.{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={cancelInternetOffer} disabled={busy}>
            Withdraw the offer
          </button>
        </div>
      ) : null}

      <div className="lease-actions panel">
        {tenancy.status === 'draft' ? (
          <>
            <button type="button" className="btn btn-primary" onClick={() => move('awaiting_signatures')}>
              Send for signature
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setDialog('edit')}>
              Edit the terms
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => move('cancelled')}>
              Cancel the draft
            </button>
          </>
        ) : null}
 
        {tenancy.status === 'awaiting_signatures' ? (
          <>
            {noSignatures ? (
              <button type="button" className="btn btn-outline" onClick={() => setDialog('edit')}>
                Edit the terms
              </button>
            ) : null}
            {!alreadySigned ? (
              <button type="button" className="btn btn-primary" onClick={() => setDialog('sign')}>
                Sign it
              </button>
            ) : null}
            <button type="button" className="btn btn-outline" onClick={() => move('draft')}>
              Withdraw for changes
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => move('cancelled')}>
              Cancel the lease
            </button>
          </>
        ) : null}

        {tenancy.status === 'active' ? (
          <>
            <button type="button" className="btn btn-primary" onClick={() => setPayingCharge({})}>
              Record a payment
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setDialog('charge')}>
              Add a charge
            </button>
            {!tenancy.internet && !tenancy.internetOffer ? (
              <button type="button" className="btn btn-outline" onClick={() => setDialog('internet-offer')}>
                Offer internet
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost" onClick={() => setDialog('end')}>
              End the tenancy
            </button>
          </>
        ) : null}

        {['ended', 'terminated'].includes(tenancy.status) ? (
          <button type="button" className="btn btn-outline" onClick={() => setPayingCharge({})}>
            Record a final payment
          </button>
        ) : null}
      </div>

      {ledger && ['active', 'ended', 'terminated'].includes(tenancy.status) ? (
        <>
          {awaitingItems.length > 0 ? (
            <section className="section-block">
              <div className="section-head">
                <h2>Tenant payments awaiting confirmation</h2>
                <span className="muted">Confirm each one before it is added to statements and receipts.</span>
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Provider</th>
                      <th>Payer phone</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {awaitingItems.map((intent) => (
                      <tr key={intent.id}>
                        <td>{formatDate(intent.createdAt)}</td>
                        <td>{intent.provider.replace(/_/g, ' ')}</td>
                        <td>{intent.payerPhone}</td>
                        <td>{formatXaf(intent.amount)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => confirmTenantPayment(intent)}
                            disabled={confirmingIntentId === intent.id}
                          >
                            {confirmingIntentId === intent.id ? 'Confirming...' : 'Confirm receipt'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <LedgerSummary totals={ledger.totals} />

          {ledger.totals.overdue > 0 ? (
            <div className="notice notice-bad">
              <strong>{formatXaf(ledger.totals.overdue)} is overdue.</strong> The oldest unpaid charge fell due on{' '}
              {formatDate(ledger.charges.find((charge) => charge.overdue)?.dueDate)}.
            </div>
          ) : null}

          <section className="section-block">
            <div className="section-head">
              <h2>Rent and charges</h2>
              <span className="muted">Rent is generated from the lease as each period starts.</span>
            </div>
            <ChargeTable charges={ledger.charges} onPay={setPayingCharge} onWaive={setWaiving} />
          </section>

          <section className="section-block">
            <div className="section-head">
              <h2>Receipts</h2>
              <Link to="/landlord/rent" className="link-strong">
                All my receipts
              </Link>
            </div>
            <PaymentTable payments={ledger.payments} onVoid={setVoiding} />
          </section>

          <DepositPanel tenancyId={tenancy.id} onChanged={reload} />
        </>
      ) : null}

      <section className="section-block">
        <div className="section-head">
          <h2>The agreement</h2>
        </div>
        <LeaseDocument tenancy={tenancy} platformName={platform?.name} />
      </section>

      {dialog === 'sign' ? (
        <SignLeaseModal
          tenancy={tenancy}
          defaultName={user.name}
          onClose={() => setDialog(null)}
          onSigned={() => {
            setDialog(null);
            reload();
          }}
        />
      ) : null}

      {dialog === 'edit' ? (
        <EditLeaseModal
          tenancy={tenancy}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            reload();
          }}
        />
      ) : null}

      {dialog === 'charge' ? (
        <AddChargeModal
          tenancy={tenancy}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            reload();
          }}
        />
      ) : null}

      {dialog === 'internet-offer' ? (
        <InternetOfferModal
          tenancy={tenancy}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            reload();
          }}
        />
      ) : null}

      {dialog === 'end' ? (
        <ReasonModal
          title="End this tenancy"
          lead={`The unit becomes free and no further rent is charged. The rent history and receipts stay, so the deposit of ${formatXaf(tenancy.deposit)} can still be settled. ${tenancy.tenant?.name} is notified.`}
          label="Why is it ending? The tenant sees this."
          confirmLabel="End the tenancy"
          tone="danger"
          onSubmit={endTenancy}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {payingCharge ? (
        <RecordPaymentModal
          tenancy={tenancy}
          charge={payingCharge.id ? payingCharge : null}
          onClose={() => setPayingCharge(null)}
          onDone={() => {
            setPayingCharge(null);
            reload();
          }}
        />
      ) : null}

      {waiving ? (
        waiving.kind === 'penalty' ? (
          <ReasonModal
            title={`Waive the ${formatXaf(waiving.outstanding)} late fee?`}
            lead={`"${waiving.label}" stops counting towards what the tenant owes. It stays on the statement marked as waived, with your reason, and the tenant is told.`}
            label="Why are you waiving it?"
            confirmLabel="Waive the late fee"
            suggestions={LATE_FEE_REASONS}
            onSubmit={waive}
            onClose={() => setWaiving(null)}
          />
        ) : (
          <ReasonModal
            title={`Write off ${formatXaf(waiving.outstanding)}?`}
            lead={`"${waiving.label}" stops counting towards what the tenant owes. It stays on the statement marked as written off.`}
            label="Why are you letting it go?"
            confirmLabel="Write it off"
            onSubmit={waive}
            onClose={() => setWaiving(null)}
          />
        )
      ) : null}

      {voiding ? (
        <ReasonModal
          title={`Cancel receipt ${voiding.receiptNumber}?`}
          lead="The receipt stays on the books marked as cancelled, and the money stops counting towards what has been paid. Use this when a cheque bounces or a payment was entered twice."
          label="Why is it being cancelled?"
          confirmLabel="Cancel the receipt"
          tone="danger"
          onSubmit={voidReceipt}
          onClose={() => setVoiding(null)}
        />
      ) : null}
    </div>
  );
};

export default LeaseDetail;
